import {
  ConsultProposalResponseDTO,
  ProposalDetailsResponseDTO
} from "@repo/core/services/entities/Proposals";
import { MobileProposalResource } from "@repo/core/services/resources/mobile-proposal";
import { ProposalsResource } from "@repo/core/services/resources/proposals";

/**
 * Resolves the client full name from mobile/desktop consult payloads.
 * Handles alternate property names (including PascalCase) returned by some API layers.
 */
export const resolveProposalClientNameFromUnknown = (
  data: Record<string, unknown>
): string => {
  const pick = (...keys: string[]): string => {
    for (const key of keys) {
      const value = data[key];
      if (value == null) continue;
      const trimmed = String(value).trim();
      if (trimmed !== "") return trimmed;
    }
    return "";
  };

  return pick(
    "strNome",
    "StrNome",
    "strNomeCompleto",
    "StrNomeCompleto",
    "strNomeReduzido",
    "StrNomeReduzido",
    "strPrimeiroNome",
    "StrPrimeiroNome"
  );
};

/**
 * Ensures `strNome` is set when the same value exists under alternate keys.
 */
export const withResolvedClientName = <
  T extends Partial<ConsultProposalResponseDTO>
>(
  proposal: T
): T & { strNome: string } => {
  const resolved = resolveProposalClientNameFromUnknown(
    proposal as Record<string, unknown>
  );
  const existing =
    proposal.strNome != null ? String(proposal.strNome).trim() : "";
  return {
    ...proposal,
    strNome: resolved || existing
  };
};

/**
 * Loads proposal from the mobile consult endpoint and fills `strNome` when missing,
 * using `/Proposta/Consultar/:id` as a fallback (same source as proposal details screen).
 */
export const fetchMobileConsultProposalMergedWithDetailsName = async (
  mobileProposalResource: MobileProposalResource,
  proposalsResource: ProposalsResource,
  lngProposta: string
): Promise<Partial<ConsultProposalResponseDTO>> => {
  const response = await mobileProposalResource.getCompleteProposal(lngProposta);

  if (!response.data) {
    throw new Error("No data received from proposal endpoint");
  }

  let merged = withResolvedClientName(response.data);

  if (!merged.strNome?.trim()) {
    try {
      const details = await proposalsResource.getProposal(lngProposta);
      const detailName = (details.data as ProposalDetailsResponseDTO | undefined)
        ?.strNome;
      if (detailName?.trim()) {
        merged = { ...merged, strNome: detailName.trim() };
      }
    } catch {
      // Fallback is optional; mobile payload is still returned
    }
  }

  return merged;
};
